import {
  fetchResponseWithToken,
  getApigeeRegionalOrigin,
} from '@adsk/acs-sc-web-platform-api';
import {
  FileUtil,
  EntityItem,
  ContentType,
  ContentDisposition,
  DocsFileTypes,
} from '@adsk/docs-js-sdk';

import { DerivativeService, Documents, Upload } from './docs';
import { Attachment, AttachmentDraft } from '../types';

export const getAttachmentType = (name: string): DocsFileTypes => {
  return FileUtil.getDocType({ name } as EntityItem);
};

export const deleteFileFromOSS = async (storageUrn: string) => {
  try {
    await Upload.removeOssFile(storageUrn);
  } catch (error) {
    throw new Error('Failed to delete file from OSS', {
      cause: error as Error,
    });
  }
};

export const uploadFileToOSS = async ({
  draft,
  projectId,
}: {
  projectId: string;
  draft: AttachmentDraft;
}): Promise<AttachmentDraft> => {
  const { file: originalFile } = draft;

  const extension = FileUtil.getFileExtension(originalFile.name);
  const filename = extension
    ? `${draft.attachmentId}.${extension}`
    : draft.attachmentId;

  const file = new File([originalFile], filename);

  const response = await Upload.uploadFile(projectId, '', file, {
    useUploadV3Api: true,
    skipCommand: true,
  });

  if (!response || !('storageUrn' in response) || !response.storageUrn) {
    throw new Error('Failed to upload attachment to OSS');
  }

  return {
    ...draft,
    storageUrn: response.storageUrn,
    originalFileName: file.name,
    createdOn: new Date().toString(),
    uploaded: true,
  };
};

export const getThumbnailURL = async (
  attachment: Attachment,
  scopes: string[] = [],
): Promise<string | undefined> => {
  try {
    const path = await DerivativeService.getThumbnailByUrn(
      attachment.versionUrn,
      { scopes, type: 'medium' },
      { skipBlob: true },
    );
    // Apigee regional origin in every env and partition (WPX-3700);
    // `getApigeeRegionalOrigin()` resolves the FedRAMP-legacy and GovCloud hosts
    // internally.
    const res = await fetchResponseWithToken(
      `${getApigeeRegionalOrigin()}${path}`,
    );
    if (res.status !== 200) return undefined;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return undefined;
  }
};

export const hasLocalView = (filename: string): boolean => {
  return (
    FileUtil.isViewableImage({
      fileName: filename,
    } as EntityItem) || FileUtil.isPdf({ fileName: filename } as EntityItem)
  );
};

export const hasLocalThumbnail = (fileName: string): boolean => {
  return FileUtil.isViewableImage({
    fileName,
  } as EntityItem);
};

export const hasThumbnail = (attachment: Attachment): boolean => {
  return (
    FileUtil.isViewableImage({
      fileName: attachment.fileName,
    } as EntityItem) ||
    FileUtil.isPdf({ fileName: attachment.fileName } as EntityItem)
  );
};

export const hasPreview = (attachment: Attachment): boolean => {
  return (
    FileUtil.isViewableImage({
      fileName: attachment.fileName,
    } as EntityItem) ||
    FileUtil.isViewableVideo({ name: attachment.fileName } as EntityItem)
  );
};

export const createAttachmentUrl = async ({
  projectId,
  fileUrn,
  filename,
  contentDisposition,
  contentType,
  errorMessage,
}: {
  projectId: string;
  fileUrn: string;
  filename: string;
  contentDisposition: ContentDisposition;
  contentType: ContentType;
  errorMessage: string;
}): Promise<string> => {
  const res = await Documents.getOssSignedCookies(projectId, {
    urn: fileUrn,
    name: filename,
    contentDisposition,
    contentType,
  });

  if (res.status !== 'complete') {
    throw new Error(errorMessage);
  }

  return res.url;
};
